// Modules
import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { isAuthenticatedSelector } from 'store/actions/Auth/selectors'
import { addShownSignal, setSignals } from 'store/actions/Signals/actions'
import { shownSignalsSelector } from 'store/actions/Signals/selectors'
// Store
import { getUnreadSignalsAPI } from 'store/api/signals'

// UI
import { Box, CloseButton, Flex, HStack, Text, VStack, useToast } from '@chakra-ui/react'

const POLLING_INTERVAL = 60000 // 60 seconds

const useSignalsPolling = () => {
  const dispatch = useDispatch()
  const toast = useToast()
  const shownSignals = useSelector(shownSignalsSelector)
  const isAuthenticated = useSelector(isAuthenticatedSelector)

  // Держим shownSignals в ref, чтобы fetchSignals не пересоздавался на каждый
  // показанный сигнал (иначе интервал перезапускается и делает лишние запросы)
  const shownSignalsRef = useRef(shownSignals)

  useEffect(() => {
    shownSignalsRef.current = shownSignals
  }, [shownSignals])

  const createSignalKey = useCallback((signal) => {
    return `${signal.currency}-${signal.symbol}-${signal.command}`
  }, [])

  const fetchSignals = useCallback(async () => {
    try {
      console.log('[useSignalsPolling] Fetching signals...')
      const response = await getUnreadSignalsAPI()
      console.log('[useSignalsPolling] Response:', response)

      if (response.success && Array.isArray(response.signals)) {
        const signals = response.signals

        dispatch(setSignals(signals))

        // Show toast for each new signal
        signals.forEach((signal) => {
          const signalKey = createSignalKey(signal)

          // Check if this signal has already been shown
          if (!shownSignalsRef.current.has(signalKey)) {
            // Determine colors based on command (BUY = green, SELL = red)
            const isBuy = signal.command === 'BUY'
            const bgColor = isBuy ? '#32A173' : '#C7434B'
            const textColor = 'white'

            // Show persistent notification
            toast({
              duration: null, // null = persistent, won't auto-close
              isClosable: true,
              position: 'bottom-left',
              render: ({ onClose }) => (
                <Box
                  bg={bgColor}
                  color={textColor}
                  p={4}
                  rounded="md"
                  shadow="lg"
                  position="relative"
                >
                  <Flex justify="space-between" align="start">
                    <VStack align="start" spacing={2} flex="1">
                      <Text fontWeight="bold" fontSize="md">
                        🔔 New Signal
                      </Text>
                      <HStack spacing={3}>
                        <Text fontSize="md">{signal.currency}</Text>
                        <Text fontSize="md">{signal.symbol}</Text>
                        <Text fontSize="md" fontWeight="bold">
                          {signal.command}
                        </Text>
                        <Text fontSize="md">{signal.volume_lots}</Text>
                      </HStack>
                    </VStack>
                    <CloseButton onClick={onClose} size="sm" color={textColor} ml={2} />
                  </Flex>
                </Box>
              ),
            })

            // Mark this signal as shown
            dispatch(addShownSignal(signalKey))
          }
        })
      }
    } catch (error) {
      console.error('Error fetching signals:', error)
    }
  }, [dispatch, toast, createSignalKey])

  useEffect(() => {
    fetchSignals()

    // Setup polling interval
    const interval = setInterval(() => {
      fetchSignals()
    }, POLLING_INTERVAL)

    // Cleanup on unmount
    return () => {
      clearInterval(interval)
    }
  }, [fetchSignals, isAuthenticated])
}

export default useSignalsPolling
